import type {
  DealDisposition,
  DealLeadDetail,
  DealLeadListItem,
} from '@/types/api'

type ScreeningOutcome = NonNullable<DealLeadListItem['screeningResult']>
type TransactionDirection = NonNullable<DealLeadDetail['direction']>

const DISPOSITION_LABELS: Record<DealDisposition, string> = {
  UNREVIEWED: 'Not reviewed',
  PURSUE: 'Pursue',
  PASS: 'Pass',
  NEEDS_INFORMATION: 'Needs information',
  ARCHIVED: 'Archived',
}

const SCREENING_OUTCOME_LABELS: Record<ScreeningOutcome, string> = {
  PASS: 'Met',
  FAIL: 'Not met',
  UNKNOWN: 'Missing information',
  NOT_APPLICABLE: 'Not applicable',
}

const DIRECTION_LABELS: Record<TransactionDirection, string> = {
  SELL_SIDE_OPPORTUNITY: 'Business for sale',
  BUY_SIDE_INQUIRY: 'Buy-side inquiry',
  NOT_A_LEAD: 'Not a lead',
  UNKNOWN: 'Not determined',
}

const RULE_LABELS: Record<string, string> = {
  DENY: 'Excluded value',
  ALLOW: 'Permitted value',
  MAX: 'Upper limit',
  MIN: 'Lower limit',
  EQUALS: 'Required value',
  RANGE: 'Permitted range',
}

export const FIELD_LABELS: Record<string, string> = {
  companyName: 'Company',
  projectName: 'Project name',
  sector: 'Sector',
  subSector: 'Sub-sector',
  geography: 'Geography',
  country: 'Country',
  description: 'Description',
  revenue: 'Revenue',
  revenueGrowthPct: 'Revenue growth',
  ebitda: 'EBITDA',
  ebitdaMarginPct: 'EBITDA margin',
  grossMarginPct: 'Gross margin',
  netProfit: 'Net profit',
  askingPrice: 'Asking price',
  valuationMultiple: 'Valuation multiple',
  largestCustomerPct: 'Largest customer',
  stateRevenueConcentrationPct: 'Single-state revenue',
  rolloverEquityPct: 'Rollover equity',
  direction: 'Direction',
  transactionType: 'Transaction type',
  fundingType: 'Funding type',
  reasonForSale: 'Reason for sale',
  timingNote: 'Timing',
  timingDeadline: 'Deadline',
  brokerCompany: 'Broker company',
  brokerName: 'Broker',
  brokerEmail: 'Broker email',
  // Must match FIELD_LABELS in compass-be criterion-field-labels.ts; nothing enforces it.
  sectorTags: 'Sector classification',
  businessModelTags: 'Business model classification',
  transactionTags: 'Transaction classification',
}

export function humanizeConstant(value: string): string {
  const words = value
    .trim()
    .split(/[\s_]+/)
    // Split camelCase before lowercasing, or field keys read as one word.
    .flatMap((chunk) =>
      /^[A-Z0-9]+$/.test(chunk) ? [chunk] : chunk.split(/(?=[A-Z])/)
    )
    .map((word) => word.toLowerCase())
    .filter(Boolean)

  if (words.length === 0) return value
  const [first, ...rest] = words
  return [first.charAt(0).toUpperCase() + first.slice(1), ...rest].join(' ')
}

const DISPOSITION_ACTION_LABELS: Partial<Record<DealDisposition, string>> = {
  PASS: 'Pass on this deal',
}

export function dispositionLabel(value: DealDisposition): string {
  return DISPOSITION_LABELS[value] ?? humanizeConstant(value)
}

export function dispositionActionLabel(value: DealDisposition): string {
  return DISPOSITION_ACTION_LABELS[value] ?? dispositionLabel(value)
}

export function screeningOutcomeLabel(value: ScreeningOutcome): string {
  return SCREENING_OUTCOME_LABELS[value] ?? humanizeConstant(value)
}

export function directionLabel(value: TransactionDirection): string {
  return DIRECTION_LABELS[value] ?? humanizeConstant(value)
}

export function ruleLabel(value: string): string {
  return RULE_LABELS[value] ?? humanizeConstant(value)
}

export function fieldLabel(key: string): string {
  return FIELD_LABELS[key] ?? humanizeConstant(key)
}

export function tagLabel(value: string): string {
  return humanizeConstant(value)
}

export const PROCESSING_STAGES = [
  { id: 'PARSE', label: 'Reading the email' },
  { id: 'SPLIT', label: 'Finding the deals inside it' },
  { id: 'EXTRACT', label: 'Pulling out the figures' },
  { id: 'RECONCILE', label: 'Checking for duplicates' },
  { id: 'SCREEN', label: 'Screening against the criteria' },
] as const

export interface StageProgress {
  label: string
  done: number
}

// All five done means a re-screen, so the last stage is reported as running.
export function stageProgress(
  completedStages: readonly string[]
): StageProgress {
  const done = new Set(completedStages)
  const running = PROCESSING_STAGES.find((stage) => !done.has(stage.id))

  if (!running) {
    const last = PROCESSING_STAGES[PROCESSING_STAGES.length - 1]
    return { label: last.label, done: PROCESSING_STAGES.length - 1 }
  }

  return {
    label: running.label,
    done: PROCESSING_STAGES.filter((stage) => done.has(stage.id)).length,
  }
}
